/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import * as React from "react";

const inputVariants = cva(
  "flex w-full rounded-[var(--radius-button)] border bg-surface-input px-3 py-2 text-sm text-text-primary transition-[color,background-color,border-color,box-shadow] file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)] focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:bg-surface-secondary-nav disabled:opacity-60 aria-[invalid=true]:border-[var(--status-danger)] aria-[invalid=true]:focus-visible:ring-[var(--status-danger)]",
  {
    variants: {
      variant: {
        default:
          "border-[var(--border-hairline)] focus-visible:border-[var(--border-focus)]",
        error:
          "border-[var(--status-danger)] focus-visible:border-[var(--status-danger)] focus-visible:ring-[var(--status-danger)]",
        success:
          "border-[var(--status-positive)] focus-visible:border-[var(--status-positive)] focus-visible:ring-[var(--status-positive)]",
      },
      inputSize: {
        sm: "h-8 text-xs px-2",
        default: "h-9 text-sm",
        lg: "h-10 px-3 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  },
);

export interface InputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  error?: string;
  success?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  valuePrefix?: React.ReactNode;
  valueSuffix?: React.ReactNode;
  showPasswordToggle?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      inputSize,
      type,
      error,
      success,
      isLoading,
      leftIcon,
      rightIcon,
      valuePrefix,
      valueSuffix,
      showPasswordToggle = false,
      disabled,
      "aria-describedby": ariaDescribedBy,
      id,
      style,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const autoId = React.useId();

    // Only use auto-generated ID when we actually need it (for error/success messages)
    // If no id is provided and no error/success, we don't need an ID, avoiding hydration issues
    const needsId = Boolean(id ?? error ?? success);
    const internalId = id ?? (needsId ? autoId : undefined);

    // Determine final variant based on error/success state
    const finalVariant = error ? "error" : success ? "success" : variant;

    // Handle password visibility toggle
    const finalType = type === "password" && showPassword ? "text" : type;
    const shouldShowPasswordToggle = showPasswordToggle && type === "password";
    const hasLeftAdornment = [leftIcon, valuePrefix].some(Boolean);
    const hasRightAdornment = [
      valueSuffix,
      rightIcon,
      shouldShowPasswordToggle,
      isLoading,
      error,
      success,
    ].some(Boolean);

    // Build ARIA describedby
    const describedByIds = [];
    if (ariaDescribedBy) describedByIds.push(ariaDescribedBy);
    if (error && internalId) describedByIds.push(`${internalId}-error`);
    if (success && internalId) describedByIds.push(`${internalId}-success`);

    const inputElement = (
      <input
        ref={ref}
        {...(internalId ? { id: internalId } : {})}
        type={finalType}
        className={cn(
          inputVariants({ variant: finalVariant, inputSize }),
          {
            "pl-7": valuePrefix && !leftIcon,
            "pl-9": leftIcon && !valuePrefix,
            "pl-14": leftIcon && valuePrefix,
            /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean truthiness check */
            "pr-9": hasRightAdornment && !valueSuffix,
            "pr-12":
              valueSuffix && !rightIcon && !isLoading && !error && !success,
            "pr-16":
              valueSuffix &&
              (rightIcon ||
                shouldShowPasswordToggle ||
                isLoading ||
                error ||
                success),
            /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
          },
          className,
        )}
        style={style}
        disabled={disabled ?? isLoading}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={
          describedByIds.length > 0 ? describedByIds.join(" ") : undefined
        }
        {...props}
      />
    );

    return (
      <div>
        <div className="relative">
          {/* Non-editable leading adornments such as icons or currency symbols */}
          {hasLeftAdornment && (
            <div className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-text-muted">
              {leftIcon}
              {valuePrefix && (
                <span
                  className={cn("text-sm font-medium", leftIcon && "ml-1")}
                  aria-hidden="true"
                >
                  {valuePrefix}
                </span>
              )}
            </div>
          )}

          {inputElement}

          {/* Right side icons/indicators */}
          <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center space-x-1">
            {/* Non-editable trailing adornments such as percent symbols */}
            {valueSuffix && (
              <span
                className="text-sm font-medium text-text-muted"
                aria-hidden="true"
              >
                {valueSuffix}
              </span>
            )}

            {/* Loading spinner */}
            {isLoading && (
              <div
                role="status"
                aria-label="Loading"
                className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--border-hairline)] border-t-brand"
              />
            )}

            {/* Error icon */}
            {error && !isLoading && (
              <AlertCircle
                className="h-4 w-4 text-[var(--status-danger)]"
                aria-hidden="true"
              />
            )}

            {/* Success icon */}
            {success && !isLoading && !error && (
              <CheckCircle2
                className="h-4 w-4 text-[var(--status-positive)]"
                aria-hidden="true"
              />
            )}

            {/* Password toggle */}
            {shouldShowPasswordToggle && !isLoading && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="pointer-events-auto text-text-muted transition-colors hover:text-text-primary"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            )}

            {/* Custom right icon */}
            {rightIcon &&
              !shouldShowPasswordToggle &&
              !isLoading &&
              !error &&
              !success && <div className="text-text-muted">{rightIcon}</div>}
          </div>
        </div>

        {/* Error message */}
        {error && internalId && (
          <p
            id={`${internalId}-error`}
            className="mt-1 text-sm text-[var(--status-danger)] flex items-center gap-1"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}

        {/* Success message */}
        {success && !error && internalId && (
          <p
            id={`${internalId}-success`}
            className="mt-1 text-sm flex items-center gap-1 text-[var(--status-positive)]"
            aria-live="polite"
          >
            <CheckCircle2
              className="h-3 w-3 flex-shrink-0"
              aria-hidden="true"
            />
            {success}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
